import React, { useState } from 'react';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import TablePagination from '@material-ui/core/TablePagination';

import { UiFilterTextField } from '../../ui/ui-filter-text-field';
import { UiFilterToggle } from '../../ui/ui-filter-toggle';

import { employeeName } from '../../employees/utils/employee-name';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { OrgSelect } from '../../widgets/org-select';

type InputProps = {
  orgs: Org[];
  employeeGroups: EmployeeGroup[];
  employeesForGroupId: (groupId: number) => Employee[];
  groupReassignments: { [employeeGroupId: number]: number };
  handleGroupAssignment: (
    groupId: number,
  ) => (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => void;
  isIncludingEmptyGroups: boolean;
  setIsIncludingEmptyGroups: (isIncludingEmptyGroups: boolean) => void;
};

export const GroupAssignmentTab = ({
  orgs,
  employeeGroups,
  employeesForGroupId,
  groupReassignments,
  handleGroupAssignment,
  isIncludingEmptyGroups,
  setIsIncludingEmptyGroups,
}: InputProps) => {
  const pageSize = 10;
  const [groupOffset, setGroupOffset] = useState<number>(0);
  const [groupNameFilter, setGroupNameFilter] = useState<string>('');

  const handleGroupPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setGroupOffset(page * pageSize);
  };

  const handleGroupNameFilterChange = (updatedGroupNameFilter: string) => {
    setGroupNameFilter(updatedGroupNameFilter);
  };

  const handleShouldShowEmptyGroupsChange = () => {
    setIsIncludingEmptyGroups(!isIncludingEmptyGroups);
  };

  const getFilteredGroups = () =>
    employeeGroups.filter(
      ({ employeeGroupId, name }) =>
        name.match(new RegExp(`${groupNameFilter}`, 'i')) &&
        (isIncludingEmptyGroups || employeesForGroupId(employeeGroupId).length),
    );

  const filteredGroups = getFilteredGroups();
  const paginatedGroups =
    filteredGroups.length > pageSize
      ? filteredGroups.slice(groupOffset, groupOffset + pageSize)
      : filteredGroups;

  return (
    <>
      <div className="card-head-no-border typo-subtitle u-margin-top">
        By default groups were imported into this organization. If there are any that should be
        moved into other organizations, assign them here. Groups that are marked as &quot;Don&apos;t
        sync&quot; will be removed. Employees that are only members of groups that were filtered
        will also be removed.
      </div>
      <div className="card">
        <div className="card-body u-display-flex u-alignItems-flexStart">
          <UiFilterTextField
            label="Group Name"
            value={groupNameFilter}
            onChange={handleGroupNameFilterChange}
          />
          <UiFilterToggle
            className="u-margin-left"
            label={`${isIncludingEmptyGroups ? 'Exclude' : 'Include'} Empty Groups`}
            value={isIncludingEmptyGroups}
            onChange={handleShouldShowEmptyGroupsChange}
          />
        </div>
      </div>
      {paginatedGroups.map(({ employeeGroupId, name }) => {
        const selectedOrg = groupReassignments[employeeGroupId];
        return (
          <div
            key={`${employeeGroupId}-card`}
            className="card u-margin-top u-display-flex u-justifyContent-spaceBetween"
          >
            <Accordion className="u-width-80 u-boxShadow-none">
              <AccordionSummary
                expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}
              >
                <div className="typo-title">{name}</div>
              </AccordionSummary>
              <AccordionDetails className="u-display-flex u-flexWrap-wrap">
                {employeesForGroupId(employeeGroupId).map((employee) => (
                  <Chip
                    className="u-margin-top-xs u-margin-bottom-xs u-margin-right-sm"
                    key={employee.employeeId}
                    size="small"
                    color="primary"
                    label={employeeName(employee)}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
            <OrgSelect
              className="u-width-20 u-margin-right"
              disableUnderline
              extraOptions={[
                <MenuItem key="None" value="None">
                  <div className="orgDropdown-nameContainer">
                    <div className="orgDropdown-name">Don&apos;t sync</div>
                  </div>
                </MenuItem>,
              ]}
              onChange={handleGroupAssignment(employeeGroupId)}
              orgOptions={orgs}
              value={selectedOrg || 'None'}
            />
          </div>
        );
      })}
      {filteredGroups.length >= pageSize && (
        <TablePagination
          component="div"
          count={filteredGroups.length}
          rowsPerPage={pageSize}
          page={groupOffset / pageSize}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onPageChange={handleGroupPageChange}
          rowsPerPageOptions={[pageSize]}
        />
      )}
    </>
  );
};
